"use client"
import React, { useCallback, useMemo, useState } from "react"
import { toast } from "sonner"
import { Toast } from "."
import { Toaster } from "./Toaster"
import type { ShowMessageOptions, ToastT } from "./types"
import { ToastContext, ToastContextType, useToasts } from "./useToasts"

const LONG_TOAST_DURATION_MS = 6_000

type Props = {
  children: React.ReactNode

  xOffset?: ToastContextType["toastXOffset"]
  yOffset?: ToastContextType["toastYOffset"]
}

export const ToastProvider = ({ children, xOffset, yOffset }: Props) => {
  const [toastYOffset, setToastYOffset] = useState<number | undefined>(yOffset)
  const [toastXOffset, setToastXOffset] = useState<number | undefined>(xOffset)
  const { withinToastProvider } = useToasts()
  const resetToastYOffset = useCallback(() => setToastYOffset(undefined), [])
  const resetToastXOffset = useCallback(() => setToastXOffset(undefined), [])

  const addToast = useCallback(
    ({
      title,
      description,
      icon,
      action,
      duration,
      dismissible,
      onClick,
    }: ToastT) =>
      toast.custom(
        () => (
          <Toast onClick={onClick}>
            <Toast.Content>
              <Toast.Title>{title}</Toast.Title>
              {description && (
                <Toast.Description>{description}</Toast.Description>
              )}
            </Toast.Content>
            {icon && <Toast.Side>{icon}</Toast.Side>}
            {action && <Toast.Action>{action}</Toast.Action>}
          </Toast>
        ),
        {
          duration,
          dismissible,
        },
      ),
    [],
  )

  const removeToast = useCallback((id: ToastT["id"]) => {
    toast.dismiss(id)
  }, [])

  const showErrorMessage = useCallback(
    (
      message: React.ReactNode,
      {
        duration = LONG_TOAST_DURATION_MS,
        dismissible = true,
        ...rest
      }: ShowMessageOptions = {},
    ) =>
      addToast({
        title: message,
        icon: <Toast.ErrorIcon />,
        duration,
        dismissible,
        ...rest,
      }),
    [addToast],
  )

  const showSuccessMessage = useCallback(
    (
      message: React.ReactNode,
      {
        duration = LONG_TOAST_DURATION_MS,
        dismissible = true,
        ...rest
      }: ShowMessageOptions = {},
    ) =>
      addToast({
        title: message,
        icon: <Toast.SuccessIcon />,
        duration,
        dismissible,
        ...rest,
      }),
    [addToast],
  )

  const value = useMemo(
    () => ({
      addToast,
      removeToast,
      setToastXOffset,
      setToastYOffset,
      showErrorMessage,
      showSuccessMessage,
      toastXOffset,
      toastYOffset,
      resetToastXOffset,
      resetToastYOffset,
      withinToastProvider: true,
      addCustomToast: toast.custom,
    }),
    [
      addToast,
      removeToast,
      showErrorMessage,
      showSuccessMessage,
      toastXOffset,
      toastYOffset,
      resetToastXOffset,
      resetToastYOffset,
    ],
  )

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Render a max of one Toaster at a time */}
      {!withinToastProvider && <Toaster />}
    </ToastContext.Provider>
  )
}
